import { css } from 'aphrodite';
import { useCallback, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { Button } from '../../components-v2/Button';
import { Dialog, DialogBody, DialogPopup } from '../../components-v2/Dialog';
import { IMAGE_DIMENSIONS } from './constants';
import { styleSheet } from './styles';
import { type CropArea, cropImage } from './utils';

const ASPECT_RATIO = IMAGE_DIMENSIONS.width / IMAGE_DIMENSIONS.height;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.01;

interface BackgroundImageCropModalProps {
	open: boolean;
	imageUrl: string;
	onOpenChange: (open: boolean) => void;
	onCropComplete: (croppedFile: File) => void;
}

export const BackgroundImageCropModal = ({
	open,
	imageUrl,
	onOpenChange,
	onCropComplete,
}: BackgroundImageCropModalProps) => {
	const [crop, setCrop] = useState({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(1);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null);
	const [isApplying, setIsApplying] = useState(false);

	const handleCropComplete = useCallback((_croppedArea: Area, croppedAreaPx: Area) => {
		setCroppedAreaPixels(croppedAreaPx);
	}, []);

	const handleApply = async () => {
		if (!croppedAreaPixels) return;

		setIsApplying(true);
		try {
			const croppedFile = await cropImage(imageUrl, croppedAreaPixels);
			onCropComplete(croppedFile);
			onOpenChange(false);
		} catch {
			// cropImage rejection is already descriptive; let the caller handle upload errors
		} finally {
			setIsApplying(false);
		}
	};

	const handleCancel = () => {
		setCrop({ x: 0, y: 0 });
		setZoom(1);
		onOpenChange(false);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup styles={[styleSheet.cropDialogPopup]} showCloseButton>
				<DialogBody>
					<h3 className={css(styleSheet.cropTitle)}>Adjust Background Image</h3>

					<div className={css(styleSheet.cropContainer)}>
						<Cropper
							image={imageUrl}
							crop={crop}
							zoom={zoom}
							aspect={ASPECT_RATIO}
							onCropChange={setCrop}
							onZoomChange={setZoom}
							onCropComplete={handleCropComplete}
							minZoom={MIN_ZOOM}
							maxZoom={MAX_ZOOM}
							objectFit='contain'
						/>
					</div>

					<div className={css(styleSheet.cropControls)}>
						<span className={css(styleSheet.cropZoomLabel)}>Zoom</span>
						<input
							type='range'
							min={MIN_ZOOM}
							max={MAX_ZOOM}
							step={ZOOM_STEP}
							value={zoom}
							onChange={e => setZoom(Number(e.target.value))}
							className={css(styleSheet.cropZoomSlider)}
							aria-label='Zoom'
						/>
					</div>

					<p className={css(styleSheet.cropHint)}>Drag to reposition. Scroll or use the slider to zoom.</p>

					<div className={css(styleSheet.cropActions)}>
						<Button variant='secondary' onClick={handleCancel} disabled={isApplying}>
							Cancel
						</Button>
						<Button onClick={handleApply} disabled={isApplying || !croppedAreaPixels}>
							{isApplying ? 'Applying...' : 'Apply'}
						</Button>
					</div>
				</DialogBody>
			</DialogPopup>
		</Dialog>
	);
};
